import { collection, onSnapshot } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  AssignUserCostCenterParams,
  AssignUserCostCenterResponse,
  CostCenterHistoryEntry,
  CostCenterIdResponse,
  CostCenterInconsistenciesResponse,
  CostCenterInconsistencyFindingDTO,
  CreateCostCenterParams,
  CreateCostCenterResponse,
  GetCostCenterParams,
  GetCostCenterResponse,
  ListCostCentersParams,
  ListCostCentersResponse,
  SetActiveCostCenterParams,
  UpdateCostCenterParams,
} from '@src/models/interfaces/costCenter.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

/**
 * Cost centers are written only through `costCenters.*` callables (Admin SDK).
 * List/get also go through the API so responsible-user scoping stays on the server.
 * Inconsistency findings are Admin SDK writes; the PWA subscribes for live refresh.
 */
class CostCenterRepository {
  async list(params: ListCostCentersParams): Promise<ListCostCentersResponse> {
    const fn = apiCallable<ListCostCentersParams, ListCostCentersResponse>(
      API_OPERATIONS.COST_CENTERS_LIST,
    );
    const response = await fn(params);
    return response.data;
  }

  async get(params: GetCostCenterParams): Promise<GetCostCenterResponse> {
    const fn = apiCallable<GetCostCenterParams, GetCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_GET,
    );
    const response = await fn(params);
    return response.data;
  }

  async create(params: CreateCostCenterParams): Promise<string> {
    const fn = apiCallable<CreateCostCenterParams, CreateCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_CREATE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async update(params: UpdateCostCenterParams): Promise<string> {
    const fn = apiCallable<UpdateCostCenterParams, CostCenterIdResponse>(
      API_OPERATIONS.COST_CENTERS_UPDATE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async setActive(params: SetActiveCostCenterParams): Promise<string> {
    const fn = apiCallable<SetActiveCostCenterParams, CostCenterIdResponse>(
      API_OPERATIONS.COST_CENTERS_SET_ACTIVE,
    );
    const response = await fn(params);
    return response.data.id;
  }

  async inconsistencies(organizationId: string): Promise<CostCenterInconsistenciesResponse> {
    const fn = apiCallable<{ organizationId: string }, CostCenterInconsistenciesResponse>(
      API_OPERATIONS.COST_CENTERS_INCONSISTENCIES,
    );
    const response = await fn({ organizationId });
    return response.data;
  }

  /**
   * Live findings published by cost-center inconsistency triggers (GUA-333).
   * Rules allow get/list for org readers only — same gate as the Cost Centers
   * config screen. Responsible-user scoped clients never reach this tab; if the
   * snapshot is denied the hook falls back to the callable.
   * Returns unsubscribe; DTOs are mapped in the service.
   */
  subscribeInconsistencies(
    organizationId: string,
    onChange: (docs: Array<{ id: string; data: CostCenterInconsistencyFindingDTO }>) => void,
    onError: (error: Error) => void,
  ): () => void {
    const colRef = collection(
      db,
      FSCollections.ORGANIZATIONS,
      organizationId,
      FSCollections.COST_CENTER_INCONSISTENCIES,
    );

    return onSnapshot(
      colRef,
      snapshot => {
        onChange(
          snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            data: docSnap.data() as CostCenterInconsistencyFindingDTO,
          })),
        );
      },
      error => onError(error instanceof Error ? error : new Error(String(error))),
    );
  }

  async assignUser(params: AssignUserCostCenterParams): Promise<AssignUserCostCenterResponse> {
    const fn = apiCallable<AssignUserCostCenterParams, AssignUserCostCenterResponse>(
      API_OPERATIONS.COST_CENTERS_ASSIGN_USER,
    );
    const response = await fn(params);
    return response.data;
  }

  async listUserHistory(
    organizationId: string,
    userId: string,
  ): Promise<{ entries: CostCenterHistoryEntry[] }> {
    const fn = apiCallable<
      { organizationId: string; userId: string },
      { entries: CostCenterHistoryEntry[] }
    >(API_OPERATIONS.COST_CENTERS_USER_HISTORY);
    const response = await fn({ organizationId, userId });
    return response.data;
  }
}

export default CostCenterRepository;
